import { BaseComponent, BElement } from "utils/base-component"

/** Same as `--small-up` in postcss.config.js (576px). */
const SMALL_UP_MEDIA_QUERY = "(min-width: 36rem)"
const SHORT_VIEWPORT_MEDIA_QUERY = "(max-height: 1080px)"

function isBelowSmallUpViewport(): boolean {
  return !window.matchMedia(SMALL_UP_MEDIA_QUERY).matches
}

export class LocationSubheader extends BaseComponent {
  static cssClass = "location-subheader"

  private navButtons!: BElement[]
  private subNavContainers!: BElement[]
  private tabIndicator!: BElement | null
  private activeTabBg!: BElement | null
  private navList!: BElement | null
  private stickyNavWrapper!: HTMLElement | null
  private stickyNavContainer!: HTMLElement | null
  private stickyNavButton!: HTMLElement | null
  private isNavHiddenByToggle = false
  private isNavHiddenByObserver = false
  private activeTabBgHideTimeout: number | null = null

  init() {
    const tabIndicatorEl = document.querySelector(`.${this.elementClassName("tab-indicator")}`)
    this.tabIndicator = tabIndicatorEl
      ? new BElement(tabIndicatorEl, this.elementClassName("tab-indicator"))
      : null

    const activeTabBgEl = document.querySelector(`.${this.elementClassName("active-tab-bg")}`)
    this.activeTabBg = activeTabBgEl
      ? new BElement(activeTabBgEl, this.elementClassName("active-tab-bg"))
      : null

    const navListEl = document.querySelector(`.${this.elementClassName("nav-list")}`)
    this.navList = navListEl ? new BElement(navListEl, this.elementClassName("nav-list")) : null

    this.stickyNavWrapper = document.querySelector(
      `.${this.elementClassName("sticky-nav-wrapper")}`,
    ) as HTMLElement | null

    this.stickyNavContainer = document.querySelector(
      `.${this.elementClassName("sticky-nav-container")}`,
    ) as HTMLElement | null

    this.stickyNavButton = document.getElementById("sticky-nav-button")

    this.navButtons = Array.from(
      document.querySelectorAll(`.${this.elementClassName("nav-button")}`),
    ).map((el) => new BElement(el, this.elementClassName("nav-button")))

    this.subNavContainers = Array.from(
      document.querySelectorAll(`.${this.elementClassName("sub-nav-container")}`),
    ).map((el) => new BElement(el, this.elementClassName("sub-nav-container")))

    // Safety check: ensure we have buttons and containers
    if (this.navButtons.length === 0 || this.subNavContainers.length === 0) {
      return
    }

    // Use server-rendered current-page state to set initial active tab.
    this.setInitialActiveTab()

    // Position the mobile tab indicator.
    this.updateTabIndicator()

    // Set up click handlers for nav buttons
    this.navButtons.forEach((button) => {
      button.element.addEventListener("click", () => {
        const tabId = button.element.id
        if (tabId) {
          this.switchTab(tabId)
        }
      })
    })

    // Keep tab indicator in sync on resize/scroll.
    window.addEventListener("resize", () => requestAnimationFrame(() => this.updateTabIndicator()))
    this.navList?.element.addEventListener("scroll", () =>
      requestAnimationFrame(() => this.updateTabIndicator()),
    )

    // Sticky nav toggle: show/hide button when nav is in sticky position, and allow user to hide the bar
    if (this.stickyNavWrapper && this.stickyNavContainer && this.stickyNavButton) {
      type StickyNavState = {
        wasStickyActive: boolean
        hasManualToggleOverride: boolean
        prevBelowSmallUp: boolean
      }

      let ticking = false
      const stickyNavState: StickyNavState = {
        wasStickyActive: false,
        hasManualToggleOverride: false,
        prevBelowSmallUp: isBelowSmallUpViewport(),
      }
      const containerHiddenClass = `${this.elementClassName("sticky-nav-container")}--hidden`
      const buttonActiveClass = `${this.elementClassName("sticky-nav-button")}--active`
      const buttonNavHiddenClass = `${this.elementClassName("sticky-nav-button")}--nav-hidden`
      // Sidebar affiliate badges use a different sticky `top` depending on the sticky bar state
      const stickyNavShownClass = "sticky-nav-shown"
      const stickyNavHiddenClass = "sticky-nav-hidden"

      const syncAffiliateBadgesStickyTop = (isStickyActive: boolean, isNavHidden: boolean) => {
        const { classList } = document.documentElement
        classList.toggle(stickyNavShownClass, isStickyActive && !isNavHidden)
        classList.toggle(stickyNavHiddenClass, isStickyActive && isNavHidden)
      }

      const getCurrentNavHiddenState = () =>
        stickyNavState.hasManualToggleOverride
          ? this.isNavHiddenByToggle
          : this.isNavHiddenByToggle || this.isNavHiddenByObserver

      const resetToggleState = () => {
        this.isNavHiddenByToggle = false
        stickyNavState.hasManualToggleOverride = false
      }

      const applyStickyNavActiveState = (isStickyActive: boolean) => {
        this.stickyNavWrapper!.classList.toggle("active", isStickyActive)
        this.stickyNavButton!.classList.toggle(buttonActiveClass, isStickyActive)
      }

      const applyDefaultHiddenStateForViewport = (belowSmallUp: boolean) => {
        this.isNavHiddenByToggle = belowSmallUp
        stickyNavState.hasManualToggleOverride = false
      }

      const applyNavVisibility = (isStickyActive: boolean) => {
        const isNavHidden = getCurrentNavHiddenState()
        this.stickyNavButton!.classList.toggle(buttonNavHiddenClass, isNavHidden)
        this.stickyNavContainer!.classList.toggle(containerHiddenClass, isNavHidden)
        syncAffiliateBadgesStickyTop(isStickyActive, isNavHidden)
      }

      const enterStickyState = (belowSmallUp: boolean) => {
        const hasCrossedViewportBreakpoint = belowSmallUp !== stickyNavState.prevBelowSmallUp
        if (!stickyNavState.wasStickyActive || hasCrossedViewportBreakpoint) {
          applyDefaultHiddenStateForViewport(belowSmallUp)
        }
        stickyNavState.wasStickyActive = true
        applyStickyNavActiveState(true)
        applyNavVisibility(true)
      }

      const exitStickyState = () => {
        stickyNavState.wasStickyActive = false
        applyStickyNavActiveState(false)
        this.stickyNavButton!.classList.remove(buttonNavHiddenClass)
        this.stickyNavContainer!.classList.remove(containerHiddenClass)
        resetToggleState()
        syncAffiliateBadgesStickyTop(false, false)
      }

      const updateStickyState = () => {
        const { top } = this.stickyNavWrapper!.getBoundingClientRect()
        // Nav is in sticky position when its top edge has reached the viewport (top <= 8px)
        const isStickyActive = top <= 8
        const belowSmallUp = isBelowSmallUpViewport()

        if (isStickyActive) {
          enterStickyState(belowSmallUp)
        } else {
          exitStickyState()
        }

        stickyNavState.prevBelowSmallUp = belowSmallUp
        ticking = false
      }

      const onScroll = () => {
        if (!ticking) {
          ticking = true
          window.requestAnimationFrame(updateStickyState)
        }
      }

      this.stickyNavButton.addEventListener("click", () => {
        if (!this.stickyNavButton!.classList.contains(buttonActiveClass)) {
          return
        }
        const wasNavHidden = getCurrentNavHiddenState()
        stickyNavState.hasManualToggleOverride = true
        this.isNavHiddenByToggle = !wasNavHidden
        applyNavVisibility(true)
        // rAF: class/transform on the sticky container updates layout; sync after that frame.
        requestAnimationFrame(() => {
          applyNavVisibility(true)
        })
      })

      const forecastTableContent = document.querySelector(".forecast-table__content")
      if (forecastTableContent) {
        const shortViewportQuery = window.matchMedia(SHORT_VIEWPORT_MEDIA_QUERY)
        const syncObserverViewportState = () => {
          if (!shortViewportQuery.matches) {
            this.isNavHiddenByObserver = false
            if (this.stickyNavWrapper!.classList.contains("active")) {
              applyNavVisibility(true)
            }
          }
        }

        const visibilityThreshold = 0.65
        const observerOptions = {
          threshold: visibilityThreshold,
          // Shrink viewport bottom so table must move further into view before hiding nav.
          rootMargin: "0px 0px -30% 0px",
        }
        // eslint-disable-next-line compat/compat
        const observer = new IntersectionObserver(([entry]) => {
          if (stickyNavState.hasManualToggleOverride) {
            return
          }
          if (!shortViewportQuery.matches) {
            this.isNavHiddenByObserver = false
          } else {
            // Hide nav only when forecast table is mostly in view.
            this.isNavHiddenByObserver = entry.intersectionRatio >= visibilityThreshold
          }
          if (this.stickyNavWrapper!.classList.contains("active")) {
            applyNavVisibility(true)
          }
        }, observerOptions)

        shortViewportQuery.addEventListener("change", syncObserverViewportState)
        syncObserverViewportState()
        observer.observe(forecastTableContent)
      }

      updateStickyState()
      window.addEventListener("scroll", onScroll, { passive: true })
      window.addEventListener("resize", onScroll)
    }
  }

  private setInitialActiveTab() {
    const currentPageButton =
      this.navButtons.find((button) => button.element.classList.contains("current-page"))
        ?.element || null
    const initialTabId = currentPageButton?.id || this.navButtons[0]?.element.id
    if (initialTabId) {
      this.switchTab(initialTabId)
    }
  }

  /**
   * Switches to a specific tab by ID
   * @param tabId - The ID of the tab to activate
   */
  private switchTab(tabId: string) {
    const previousActiveButton =
      this.navButtons.find((button) => button.element.classList.contains("active"))?.element || null

    // Remove active class from all buttons and containers
    this.navButtons.forEach((button) => {
      button.element.classList.toggle("active", button.element.id === tabId)
    })

    this.subNavContainers.forEach((container) => {
      const isActive = container.element.id === tabId
      container.element.classList.toggle("active", isActive)

      // Also toggle active on the sub-nav-content inside
      const content = container.element.querySelector(
        `.${this.elementClassName("sub-nav-content")}`,
      )
      if (content) {
        content.classList.toggle("active", isActive)
      }
    })

    // Update tab indicator position
    this.updateTabIndicator()

    const nextActiveButton =
      this.navButtons.find((button) => button.element.id === tabId)?.element || null
    this.animateActiveTabBg(previousActiveButton, nextActiveButton)
  }

  /**
   * Updates the tab indicator position to match the active nav-button.
   */
  private updateTabIndicator() {
    if (!this.tabIndicator) return

    const activeButton = this.navButtons.find((b) => b.element.classList.contains("active"))
    if (!activeButton) {
      this.tabIndicator.element.style.width = "0"
      return
    }

    const navItem = activeButton.element.closest(`.${this.elementClassName("nav-item")}`)
    const locationTabs = this.tabIndicator.element.parentElement
    if (!navItem || !locationTabs) return

    const tabsRect = locationTabs.getBoundingClientRect()
    const itemRect = (navItem as HTMLElement).getBoundingClientRect()

    this.tabIndicator.element.style.left = `${itemRect.left - tabsRect.left - 24}px`
    this.tabIndicator.element.style.width = `${itemRect.width + 48}px`
  }

  private animateActiveTabBg(fromButton: HTMLElement | null, toButton: HTMLElement | null) {
    if (!this.activeTabBg || !fromButton || !toButton || fromButton === toButton) return

    const locationTabs = this.activeTabBg.element.parentElement
    if (!locationTabs) return
    const animatingClass = `${this.elementClassName("location-tabs")}--animating-active-tab`

    const tabsRect = locationTabs.getBoundingClientRect()
    const fromRect = fromButton.getBoundingClientRect()
    const toRect = toButton.getBoundingClientRect()

    const bg = this.activeTabBg.element
    bg.style.transition = "none"
    bg.style.top = `${fromRect.top - tabsRect.top}px`
    bg.style.left = `${fromRect.left - tabsRect.left}px`
    bg.style.width = `${fromRect.width}px`
    bg.style.height = `${fromRect.height}px`
    bg.style.opacity = "1"

    // Force style/layout flush before enabling transition.
    bg.getBoundingClientRect()

    locationTabs.classList.add(animatingClass)
    bg.style.removeProperty("transition")
    bg.style.top = `${toRect.top - tabsRect.top}px`
    bg.style.left = `${toRect.left - tabsRect.left}px`
    bg.style.width = `${toRect.width}px`
    bg.style.height = `${toRect.height}px`

    if (this.activeTabBgHideTimeout !== null) {
      window.clearTimeout(this.activeTabBgHideTimeout)
    }
    this.activeTabBgHideTimeout = window.setTimeout(() => {
      bg.style.opacity = "0"
      locationTabs.classList.remove(animatingClass)
      this.activeTabBgHideTimeout = null
    }, 260)
  }
}
